import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Loader2, PartyPopper, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ProfilePicture } from "~/components/profile/profile-picture";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Form, FormField, FormItem, FormMessage } from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { userApi } from "~/lib/api/user";
import { cn } from "~/lib/utils";

const formSchema = z.object({
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	username: z.string().optional(),
	profilePicture: z.instanceof(File).optional().nullable(),
});

export const Route = createFileRoute("/_protected/onboarding")({
	component: Onboarding,
});

function Onboarding() {
	const router = useRouter();
	const client = useQueryClient();

	const onboardUser = useMutation({
		mutationFn: (data: z.infer<typeof formSchema>) =>
			userApi.onboardUser({
				firstName: data.firstName,
				lastName: data.lastName,
				profilePicture: data.profilePicture || null,
				username: data.username,
			}),
		onSuccess: async () => {
			await client.refetchQueries({ queryKey: ["user", "partial"] });
			await router.invalidate();
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			firstName: "",
			lastName: "",
			username: "",
			profilePicture: null,
		},
		disabled: onboardUser.isPending,
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		await onboardUser.mutateAsync(values);
	}

	return (
		<div className="min-h-screen bg-black text-primary-foreground lg:flex">
			{/* Welcome Section */}
			<div className="lg:w-1/2 lg:flex lg:items-center lg:justify-center lg:p-12">
				<div className="p-6 lg:p-0 text-center lg:text-left max-w-md mx-auto">
					<div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-6">
						<PartyPopper className="w-4 h-4 text-primary-foreground animate-bounce" />
						<span className="text-sm font-medium text-accent tracking-wider">
							Let's Get You Set Up!
						</span>
						<Sparkles className="w-4 h-4 text-primary-foreground animate-pulse" />
					</div>
					<h1 className="text-[2.5rem] lg:text-[3.5rem] leading-none font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-accent/80 tracking-wide mb-4 font-heading">
						Welcome to the Party!
					</h1>
					<p className="hidden lg:block text-base lg:text-lg text-secondary leading-relaxed mb-8 lg:mb-0">
						We're thrilled to have you here! Get ready to create unforgettable
						moments and bring people together for amazing events.
					</p>
				</div>
			</div>

			{/* Form Section */}
			<div className="flex-1 flex items-center justify-center px-6 pb-6 lg:p-12">
				<div className="w-full max-w-[400px]">
					<Card className="bg-primary/10 border-white/10 backdrop-blur-sm shadow-2xl shadow-accent/20">
						<CardHeader className="space-y-2 text-center lg:text-left">
							<div className="flex items-center gap-2 justify-center lg:justify-start mb-2">
								<Sparkles className="w-5 h-5 text-accent animate-pulse" />
								<h2 className="text-2xl font-semibold tracking-wide text-primary-foreground font-heading">
									Time to Shine!
								</h2>
							</div>
							<p className="text-secondary">
								Let's make your profile as awesome as you are
							</p>
						</CardHeader>
						<CardContent className="space-y-6 pb-8">
							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(onSubmit)}
									className="space-y-6"
								>
									<div className="mb-12">
										<ProfilePicture
											onChange={(file) => form.setValue("profilePicture", file)}
										/>
									</div>
									<div className="space-y-4">
										<FormField
											control={form.control}
											name="firstName"
											render={({ field }) => (
												<FormItem>
													<Label
														htmlFor="firstName"
														className="text-white flex items-center gap-2"
													>
														What should we call you?
													</Label>
													<Input
														id="firstName"
														placeholder="Your awesome first name"
														className={cn(
															"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
															field.disabled && "opacity-50",
														)}
														disabled={field.disabled}
														{...field}
													/>
													<FormMessage className="text-red-500 text-sm mt-1" />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="lastName"
											render={({ field }) => (
												<FormItem>
													<Label
														htmlFor="lastName"
														className="text-white flex items-center gap-2"
													>
														What's your last name?
													</Label>
													<Input
														id="lastName"
														placeholder="Your cool last name"
														className={cn(
															"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
															field.disabled && "opacity-50",
														)}
														disabled={field.disabled}
														{...field}
													/>
													<FormMessage className="text-red-500 text-sm mt-1" />
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="username"
											render={({ field }) => (
												<FormItem>
													<Label
														htmlFor="username"
														className="text-white flex items-center gap-2"
													>
														What's your username?
													</Label>
													<Input
														id="username"
														placeholder="awesome_name"
														className={cn(
															"bg-primary/10 border-white/10 text-white placeholder:text-white/50 focus:border-accent/50 focus:ring-accent/20",
															field.disabled && "opacity-50",
														)}
														disabled={field.disabled}
														{...field}
													/>
													<FormMessage className="text-red-500 text-sm mt-1" />
												</FormItem>
											)}
										/>
									</div>
									<Button
										type="submit"
										className="w-full h-11 font-medium bg-accent hover:bg-accent/90 text-accent-foreground"
										disabled={onboardUser.isPending}
									>
										<span className="relative z-10 flex items-center">
											{onboardUser.isPending ? (
												<Loader2 className="w-4 h-4 animate-spin mr-2" />
											) : null}
											Create Profile
										</span>
									</Button>
								</form>
							</Form>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
